/**
 * OnboardingPage Component
 * SESSION_199: Onboarding Frontend Step 1 - Business info editing
 * SESSION_200: Onboarding Frontend Step 2 - Contact preferences
 *
 * Guides new business owners through the onboarding flow.
 * Step 1: Review and update business information
 * Step 2: Configure notification preferences
 *
 * Flow:
 * 1. If already completed onboarding → redirect to dashboard
 * 2. If no business linked → show message
 * 3. Show Step 1: Business info editing form
 * 4. On submit → save changes, navigate to Step 2
 * 5. Show Step 2: Contact preferences form
 * 6. On submit → save preferences, mark complete, redirect to dashboard
 */

import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Title,
  Text,
  Loader,
  Center,
  Alert,
  Stepper
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconBuilding, IconBell } from '@tabler/icons-react';
import { OnboardingBusinessInfoForm, OnboardingContactPreferencesForm } from '../../components/onboarding';
import {
  useOnboardingStatusQuery,
  useUpdateBusinessByOwnerMutation,
  useCompleteOnboardingMutation,
  useUpdateContactPreferencesMutation,
  useUpdateClaimBusinessDetailsMutation
} from '../../../application/queries/onboardingQueries';
import { useAddBusinessAliasMutation } from '../../../application/queries/businessQueries';
import { CUSTOMER_ROUTES } from '../../routing/routes';
import type { UpdateBusinessByOwnerRequest, UpdateContactPreferencesRequest, OnboardingBusinessInfo } from '../../../domain/entities/Onboarding';

/**
 * OnboardingPage - Multi-step onboarding flow for business owners
 */
export const OnboardingPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();

  // Current step in onboarding (Step 0 = business info, Step 1 = contact prefs)
  const [currentStep, setCurrentStep] = useState<number>(0);

  // Fetch onboarding status
  const {
    data: onboardingStatus,
    isLoading: isLoadingStatus,
    error: statusError
  } = useOnboardingStatusQuery();

  // Mutations
  const updateBusinessMutation = useUpdateBusinessByOwnerMutation();
  const updateContactPreferencesMutation = useUpdateContactPreferencesMutation();
  const completeOnboardingMutation = useCompleteOnboardingMutation();
  // SESSION_339: Mutation for updating claim business details (pending claims)
  const updateClaimDetailsMutation = useUpdateClaimBusinessDetailsMutation();
  // S_030_C_003: Mutation for adding business aliases
  const addAliasMutation = useAddBusinessAliasMutation();

  // S_030_C_003: State for pending aliases collected from form
  const [pendingAliases, setPendingAliases] = useState<string[]>([]);

  // Redirect to dashboard if already completed onboarding
  useEffect(() => {
    if (onboardingStatus?.hasCompletedOnboarding) {
      navigate(CUSTOMER_ROUTES.DASHBOARD, { replace: true });
    }
  }, [onboardingStatus?.hasCompletedOnboarding, navigate]);

  /**
   * SESSION_339: Create business info for the form.
   * Works for both existing businesses and pending claims.
   * For pending claims, we convert the claim data to the OnboardingBusinessInfo shape.
   */
  const businessInfoForForm = useMemo((): OnboardingBusinessInfo | null => {
    // If user has a linked business, use that
    if (onboardingStatus?.business) {
      return onboardingStatus.business;
    }
    // SESSION_339: If user has a pending claim, convert to OnboardingBusinessInfo shape
    if (onboardingStatus?.pendingBusinessClaim) {
      return {
        id: onboardingStatus.pendingBusinessClaim.claimId,
        name: onboardingStatus.pendingBusinessClaim.businessName,
        phone: onboardingStatus.pendingBusinessClaim.phone,
        email: onboardingStatus.pendingBusinessClaim.email,
        website: onboardingStatus.pendingBusinessClaim.website,
        description: onboardingStatus.pendingBusinessClaim.description,
        address: onboardingStatus.pendingBusinessClaim.address
      };
    }
    return null;
  }, [onboardingStatus?.business, onboardingStatus?.pendingBusinessClaim]);

  /**
   * S_030_C_003: Submit aliases to backend after business info is saved
   */
  const submitAliases = useCallback(async (businessId: string, aliases: string[]): Promise<void> => {
    for (const alias of aliases) {
      try {
        await addAliasMutation.mutateAsync({
          businessId,
          command: { alias }
        });
      } catch (error) {
        console.error(`Failed to add alias "${alias}":`, error);
        // Continue with remaining aliases
      }
    }
  }, [addAliasMutation]);

  /**
   * S_030_C_003: Handler for collecting aliases from the form
   */
  const handleAliasesCollected = useCallback((aliases: string[]): void => {
    setPendingAliases(aliases);
  }, []);

  /**
   * Handle Step 1 (business info) submission
   * SESSION_339: Updated to handle both existing businesses and pending claims
   * S_030_C_003: Submit aliases after business update succeeds
   */
  const handleBusinessInfoSubmit = useCallback((data: UpdateBusinessByOwnerRequest): void => {
    // Case 1: User has a linked business - update the business directly
    if (onboardingStatus?.business?.id) {
      const businessId = onboardingStatus.business.id;
      updateBusinessMutation.mutate(
        {
          businessId,
          request: data
        },
        {
          onSuccess: async (): Promise<void> => {
            // S_030_C_003: Submit aliases after business update succeeds
            if (pendingAliases.length > 0) {
              await submitAliases(businessId, pendingAliases);
            }
            // Move to Step 2 (contact preferences)
            setCurrentStep(1);
          }
        }
      );
      return;
    }

    // Case 2: User has a pending claim - update the claim's business details
    // Note: Aliases are not submitted for pending claims (no business ID yet)
    if (onboardingStatus?.pendingBusinessClaim?.claimId) {
      updateClaimDetailsMutation.mutate(
        {
          claimId: onboardingStatus.pendingBusinessClaim.claimId,
          request: data
        },
        {
          onSuccess: () => {
            // Move to Step 2 (contact preferences)
            setCurrentStep(1);
          }
        }
      );
      return;
    }
  }, [onboardingStatus?.business?.id, onboardingStatus?.pendingBusinessClaim?.claimId, updateBusinessMutation, updateClaimDetailsMutation, pendingAliases, submitAliases]);

  /**
   * Handle Step 2 (contact preferences) submission
   * SESSION_200: Save preferences then complete onboarding
   */
  const handleContactPreferencesSubmit = useCallback((request: UpdateContactPreferencesRequest): void => {
    updateContactPreferencesMutation.mutate(request, {
      onSuccess: () => {
        // After saving preferences, mark onboarding as complete
        completeOnboardingMutation.mutate(undefined, {
          onSuccess: () => {
            navigate(CUSTOMER_ROUTES.DASHBOARD, { replace: true });
          }
        });
      }
    });
  }, [updateContactPreferencesMutation, completeOnboardingMutation, navigate]);

  /**
   * Handle back button in Step 2 - go back to Step 1
   */
  const handleContactPreferencesBack = useCallback((): void => {
    setCurrentStep(0);
  }, []);

  /**
   * Handle skip Step 1 (go directly to Step 2)
   */
  const handleSkipBusinessInfo = useCallback((): void => {
    setCurrentStep(1);
  }, []);

  // Loading state
  if (isLoadingStatus) {
    return (
      <Container size="sm" py="xl">
        <Center h={400}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading your onboarding...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Error state
  if (statusError) {
    return (
      <Container size="sm" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Onboarding"
          color="red"
        >
          {statusError.message || 'Failed to load onboarding status. Please refresh the page.'}
        </Alert>
      </Container>
    );
  }

  // SESSION_339: Removed blocking "Business Setup In Progress" alert
  // Users with pending claims can now complete onboarding (Step 1: business details, Step 2: contact preferences)

  // No business linked and no pending claim (edge case)
  if (!businessInfoForForm) {
    return (
      <Container size="sm" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Account Setup Issue"
          color="yellow"
        >
          Your account doesn&apos;t have a business associated with it yet. If you recently
          registered, please check your email for confirmation. Otherwise, please contact
          support for assistance.
        </Alert>
      </Container>
    );
  }

  // Already completed - redirect will happen via useEffect
  if (onboardingStatus.hasCompletedOnboarding) {
    return (
      <Container size="sm" py="xl">
        <Center h={400}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Redirecting to dashboard...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Stack gap="xs" ta="center">
          <Title order={1}>Welcome to MentionedMe!</Title>
          <Text c="dimmed" size="lg">
            Let&apos;s get your business set up in just a few steps.
          </Text>
        </Stack>

        {/* Stepper */}
        <Stepper active={currentStep}>
          <Stepper.Step
            label="Business Info"
            description="Review your details"
            icon={<IconBuilding size={18} />}
          />
          <Stepper.Step
            label="Contact Preferences"
            description="Notification settings"
            icon={<IconBell size={18} />}
          />
          <Stepper.Completed>
            <IconCheck size={18} />
          </Stepper.Completed>
        </Stepper>

        {/* Step Content */}
        {/* SESSION_339: Use businessInfoForForm which works for both existing businesses and pending claims */}
        {/* SESSION_340: Pass userPhone for form pre-population fallback */}
        {currentStep === 0 && businessInfoForForm && (
          <OnboardingBusinessInfoForm
            business={businessInfoForForm}
            onSubmit={handleBusinessInfoSubmit}
            onSkip={handleSkipBusinessInfo}
            isLoading={updateBusinessMutation.isPending || updateClaimDetailsMutation.isPending}
            userPhone={onboardingStatus?.phoneNumber}
            onAliasesCollected={handleAliasesCollected}
          />
        )}

        {/* Email moved from Business Info to Contact Preferences form */}
        {currentStep === 1 && onboardingStatus && (
          <OnboardingContactPreferencesForm
            emailNotificationsEnabled={onboardingStatus.emailNotificationsEnabled}
            smsNotificationsEnabled={onboardingStatus.smsNotificationsEnabled}
            phoneNumber={onboardingStatus.phoneNumber}
            businessPhone={businessInfoForForm?.phone ?? null}
            userEmail={onboardingStatus.userEmail}
            businessEmail={businessInfoForForm?.email ?? null}
            onSubmit={handleContactPreferencesSubmit}
            onBack={handleContactPreferencesBack}
            isLoading={updateContactPreferencesMutation.isPending || completeOnboardingMutation.isPending}
          />
        )}
      </Stack>
    </Container>
  );
};
